'use client'

import { ReactNode, useMemo } from 'react'
import { AppRouterCacheProvider } from '@mui/material-nextjs/v15-appRouter'
import { prefixer } from 'stylis'
import rtlPlugin from 'stylis-plugin-rtl'
import { ThemeKey } from './resolveTheme'
import ThemeProvider from './theme-provider'

export default function MUIRegistry({
    children,
    direction,
    themeKey
}: {
    children: ReactNode
    direction: 'rtl' | 'ltr'
    themeKey: ThemeKey
}) {
    /*
     * These options previously built a second emotion cache behind a nested CacheProvider, which
     * shadowed AppRouterCacheProvider. That inner cache had no App Router SSR integration, so on
     * the server emotion fell back to emitting a <style> tag inline beside the first element using
     * each class. The client render has no such node, so hydration failed wherever one landed —
     * the `img` and `MuiSvgIcon` mismatches seen across the app, not only on the holidays pages.
     *
     * Handing the same key and stylis plugins to AppRouterCacheProvider keeps class names and the
     * RTL transform byte-identical; only the insertion point moves, from inline to <head>.
     */
    const options = useMemo(
        () =>
            direction === 'rtl'
                ? { key: 'muirtl', stylisPlugins: [prefixer, rtlPlugin] }
                : { key: 'muiltr', stylisPlugins: [prefixer] },
        [direction]
    )

    return (
        <AppRouterCacheProvider options={options}>
            <ThemeProvider direction={direction} themeKey={themeKey}>
                {children}
            </ThemeProvider>
        </AppRouterCacheProvider>
    )
}
